import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Truck,
  FileCheck,
  Recycle,
  BarChart3,
  ClipboardCheck,
  Building2,
  Package,
  Store,
  Monitor,
  Landmark,
  Send,
  Phone,
  ChevronDown,
  Menu,
  X,
  Linkedin } from
"lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger } from
"@/components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Assets
const LOGO = "https://customer-assets.emergentagent.com/job_8a27fd21-2917-4128-b502-8e2ad7b4f186/artifacts/rr8eu6u8_EC%20full%20-%20without%20border%20%281%29.png";
const HERO_IMAGE = "https://images.unsplash.com/photo-1582748298043-0c0d31aa506e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxlbGVjdHJvbmljJTIwd2FzdGUlMjByZWN5Y2xpbmclMjBjb21wdXRlciUyMGRldmljZXMlMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzcyNzAzNzEzfDA&ixlib=rb-4.1.0&q=85";
const SERVICES_IMG_1 = "https://customer-assets.emergentagent.com/job_e9d52fdc-bdf3-4925-892d-97563baca200/artifacts/5mdmmqj5_image.png";
const SERVICES_IMG_2 = "https://customer-assets.emergentagent.com/job_e9d52fdc-bdf3-4925-892d-97563baca200/artifacts/cr0n79mx_image.png";
const CIRCUIT_TEXTURE = "https://images.unsplash.com/photo-1760842543713-108c3cadbba1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxlbGVjdHJvbmljJTIwY2lyY3VpdCUyMGJvYXJkJTIwbWFjcm8lMjBncmVlbnxlbnwwfHx8fDE3NzI3MDI1MDF8MA&ixlib=rb-4.1.0&q=85";

// Service card images
const SERVICE_IMAGES = {
  pickup: "https://customer-assets.emergentagent.com/job_e9d52fdc-bdf3-4925-892d-97563baca200/artifacts/zdy4sxqv_image.png",
  compliance: "https://images.unsplash.com/photo-1583521214690-73421a1829a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxjb21wbGlhbmNlJTIwZG9jdW1lbnRzJTIwcGFwZXJ3b3JrJTIwYnVzaW5lc3MlMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzc0MzYzMzE3fDA&ixlib=rb-4.1.0&q=85",
  logistics: "https://customer-assets.emergentagent.com/job_e9d52fdc-bdf3-4925-892d-97563baca200/artifacts/rnfppttb_image.png",
  recycling: "https://customer-assets.emergentagent.com/job_e9d52fdc-bdf3-4925-892d-97563baca200/artifacts/rtwdkf3o_image.png",
  tracking: "https://customer-assets.emergentagent.com/job_e9d52fdc-bdf3-4925-892d-97563baca200/artifacts/574p83yk_image.png"
};

// Scroll animation hook
const useScrollAnimation = (threshold = 0.1) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return { isVisible, ref };
};

// Counter animation hook
const useCounterAnimation = (end, duration = 2000, startOnView = true) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!startOnView) {
      animateCount();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          animateCount();
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated, end, duration, startOnView]);

  const animateCount = () => {
    const startTime = Date.now();
    const animate = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      const easeOutQuad = 1 - (1 - progress) * (1 - progress);
      setCount(Math.floor(easeOutQuad * end));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  };

  return { count, ref };
};

// Animated section wrapper
const AnimatedSection = ({ children, className = "", delay = 0 }) => {
  const { isVisible, ref } = useScrollAnimation(0.1);
  
  return (
    <div 
      ref={ref}
      className={`transition-all duration-700 ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transitionDelay: `${delay}ms`
      }}
    >
      {children}
    </div>
  );
};

// Header Component
const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100" data-testid="header">
      <div className="section-container py-4 flex items-center justify-between">
        <a href="/" className="flex items-center gap-3" data-testid="logo-link">
          <img src={LOGO} alt="ElectroCycle Logo" className="w-[110px] md:w-[150px] h-auto object-contain" style={{ border: 'none', outline: 'none' }} />
        </a>
        
        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          <button onClick={() => scrollToSection('services')} className="text-[#3F3F3F] hover:text-[#056839] font-medium transition-colors">Services</button>
          <button onClick={() => scrollToSection('clients')} className="text-[#3F3F3F] hover:text-[#056839] font-medium transition-colors">Clients</button>
          <button onClick={() => scrollToSection('process')} className="text-[#3F3F3F] hover:text-[#056839] font-medium transition-colors">Process</button>
          <Link to="/about" className="text-[#3F3F3F] hover:text-[#056839] font-medium transition-colors">About</Link>
          <button onClick={() => scrollToSection('faq')} className="text-[#3F3F3F] hover:text-[#056839] font-medium transition-colors">FAQ</button>
          <Button
            onClick={() => scrollToSection('contact')}
            className="btn-primary"
            data-testid="header-cta-btn">

            Request Pickup
          </Button>
        </nav>

        {/* Mobile Menu Toggle */}
        <button
          className="md:hidden p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle">

          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Nav */}
      {mobileMenuOpen &&
      <div className="md:hidden bg-white border-t border-gray-100 py-4" data-testid="mobile-nav">
          <nav className="section-container flex flex-col gap-4">
            <button onClick={() => scrollToSection('services')} className="text-left text-[#3F3F3F] hover:text-[#056839] font-medium py-2">Services</button>
            <button onClick={() => scrollToSection('clients')} className="text-left text-[#3F3F3F] hover:text-[#056839] font-medium py-2">Clients</button>
            <button onClick={() => scrollToSection('process')} className="text-left text-[#3F3F3F] hover:text-[#056839] font-medium py-2">Process</button>
            <Link to="/about" className="text-left text-[#3F3F3F] hover:text-[#056839] font-medium py-2" onClick={() => setMobileMenuOpen(false)}>About</Link>
            <button onClick={() => scrollToSection('faq')} className="text-left text-[#3F3F3F] hover:text-[#056839] font-medium py-2">FAQ</button>
            <Button
            onClick={() => scrollToSection('contact')}
            className="btn-primary w-full">

              Request Pickup
            </Button>
          </nav>
        </div>
      }
    </header>);

};

// Hero Section
const HeroSection = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  
  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="hero-gradient !pt-[150px] !pb-[30px]" data-testid="hero-section">
      <div className="section-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="order-2 lg:order-1">
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-[#3F3F3F] mb-6 transition-all duration-700" 
              style={{ 
                fontFamily: 'var(--font-heading)',
                opacity: isLoaded ? 1 : 0,
                transform: isLoaded ? 'translateY(0)' : 'translateY(20px)'
              }}
            >
              RESPONSIBLE WEEE MANAGEMENT IN ZAMBIA
            </h1>
            <p 
              className="text-base md:text-lg leading-relaxed text-gray-600 mb-8 max-w-xl transition-all duration-700 delay-150"
              style={{ 
                opacity: isLoaded ? 1 : 0,
                transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
                transitionDelay: '150ms'
              }}
            >
              ElectroCycle helps companies collect, recycle and report WEEE in compliance with environmental regulations.
            </p>
            
            {/* CTA Buttons */}
            <div 
              className="flex flex-col sm:flex-row gap-4 transition-all duration-700"
              style={{ 
                opacity: isLoaded ? 1 : 0,
                transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
                transitionDelay: '300ms'
              }}
            >
              <Button
                onClick={scrollToContact}
                className="btn-primary flex items-center gap-2 hover:scale-105 transition-transform"
                data-testid="hero-pickup-btn">
                <Truck size={20} />
                Request WEEE Pickup
              </Button>
              <Button
                onClick={scrollToContact}
                className="btn-outline flex items-center gap-2 hover:scale-105 transition-transform"
                data-testid="hero-compliance-btn">
                <Phone size={20} />
                Speak with our Experts
              </Button>
            </div>
          </div>

          {/* Right Image */}
          <div 
            className="order-1 lg:order-2 transition-all duration-700"
            style={{ 
              opacity: isLoaded ? 1 : 0,
              transform: isLoaded ? 'translateX(0)' : 'translateX(30px)',
              transitionDelay: '200ms'
            }}
          >
            <div className="aspect-container aspect-[4/3] rounded-sm overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300">
              <img
                src={HERO_IMAGE}
                alt="WEEE collection and recycling"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                data-testid="hero-image" />
            </div>
          </div>
        </div>
      </div>
    </section>);

};

// Services Section
const ServicesSection = () => {
  const services = [
  {
    icon: <Truck size={28} />,
    title: "WEEE Pickup Services",
    description: "Pickup services for all types of electronic equipment across Zambia.",
    image: SERVICE_IMAGES.pickup
  },
  {
    icon: <FileCheck size={28} />,
    title: "EPR Compliance Support",
    description: "Extended Producer Responsibility (EPR) documentation and regulatory compliance assistance. In partnership with Producer Responsibility Organization AfriCycle Zambia.",
    image: SERVICE_IMAGES.compliance
  },
  {
    icon: <Package size={28} />,
    title: "Reverse Logistics",
    description: "End-to-end logistics management for returning end-of-life electronics to proper processing.",
    image: SERVICE_IMAGES.logistics
  },
  {
    icon: <Recycle size={28} />,
    title: "Environmentally Sound Recycling",
    description: "Certified recycling processes ensuring responsible material recovery and disposal.",
    image: SERVICE_IMAGES.recycling
  },
  {
    icon: <BarChart3 size={28} />,
    title: "Digital Tracking & Reporting",
    description: "Real-time tracking and comprehensive reporting for complete transparency.",
    image: SERVICE_IMAGES.tracking
  }];


  // Get container max-width based on count
  const getMaxWidth = (count) => {
    if (count === 2) return "max-w-2xl";
    if (count === 3) return "max-w-5xl";
    if (count === 4) return "max-w-3xl";
    if (count === 5 || count === 6) return "max-w-5xl";
    return "max-w-5xl";
  };

  // Get card width class based on count and screen size
  const getCardWidth = (count) => {
    if (count === 2) return "w-full sm:w-[calc(50%-12px)]";
    if (count === 3) return "w-full sm:w-[calc(33.333%-16px)]";
    if (count === 4) return "w-full sm:w-[calc(50%-12px)]";
    if (count === 5) return "w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]";
    if (count === 6) return "w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]";
    return "w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]";
  };

  return (
    <section id="services" className="section-gap bg-white" data-testid="services-section">
      <div className="section-container">
        <AnimatedSection className="text-center mb-16">
          <p className="uppercase !font-medium !text-lg mb-3 text-[#69A488]">What We Do</p>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#3F3F3F] mb-4" style={{ fontFamily: 'var(--font-heading)' }}>END-TO-END WEEE MANAGEMENT

          </h2>
          <p className="text-base md:text-lg leading-relaxed text-gray-600 max-w-2xl mx-auto">
            ElectroCycle provides comprehensive management of Waste Electrical and Electronic Equipment (WEEE), including collection, reverse logistics, recycling and reporting.
          </p>
        </AnimatedSection>

        <div className={`flex flex-wrap justify-center gap-6 mx-auto ${getMaxWidth(services.length)}`}>
          {services.map((service, index) =>
          <AnimatedSection key={index} delay={index * 100} className={`${getCardWidth(services.length)}`}>
            <div className="service-card overflow-hidden h-full hover:scale-[1.02] hover:shadow-xl transition-all duration-300" data-testid={`service-card-${index}`}>
              <div className="h-40 -mx-8 -mt-8 mb-6 overflow-hidden">
                <img 
                  src={service.image} 
                  alt={service.title} 
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  style={{ objectPosition: (service.title === "Digital Tracking & Reporting" || service.title === "Reverse Logistics") ? "center top" : "center center" }}
                />
              </div>
              <div className="flex items-center gap-4 mb-4">
                <div className="icon-container">
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold text-[#3F3F3F]" style={{ fontFamily: 'var(--font-heading)' }}>
                  {service.title}
                </h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                {service.description}
              </p>
            </div>
          </AnimatedSection>
          )}
        </div>
      </div>
    </section>);

};

// Who We Work With Section
const ClientsSection = () => {
  const clients = [
  {
    icon: <Building2 size={32} />,
    title: "Electronics Manufacturers",
    description: "Producer responsibility compliance for local and international manufacturers."
  },
  {
    icon: <Package size={32} />,
    title: "Importers & Distributors",
    description: "EPR obligations management for companies importing electronic goods."
  },
  {
    icon: <Store size={32} />,
    title: "Retailers",
    description: "Take-back programs and collection services for electronics retailers."
  },
  {
    icon: <Monitor size={32} />,
    title: "Corporations with IT Equipment",
    description: "IT asset disposal and data destruction services for businesses."
  }];

  return (
    <section id="clients" className="section-gap bg-[#F9FAFB]" data-testid="clients-section">
      <div className="section-container">
        <AnimatedSection className="text-center mb-16">
          <p className="uppercase !font-medium !text-lg mb-3 text-[#69A488]">Who We Work With</p>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#3F3F3F] mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            SERVING ZAMBIA'S BUSINESS COMMUNITY
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-gray-600 max-w-2xl mx-auto">
            We partner with organizations across sectors to ensure responsible WEEE management and regulatory compliance.
          </p>
        </AnimatedSection>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {clients.map((client, index) =>
          <AnimatedSection key={index} delay={index * 100}>
            <div className="audience-card flex flex-col items-center text-center w-full h-full hover:scale-105 hover:shadow-lg hover:border-[#056839] transition-all duration-300" data-testid={`client-card-${index}`}>
              <div className="icon-container mb-4 group-hover:bg-[#056839] transition-colors">
                {client.icon}
              </div>
              <h3 className="text-lg font-semibold text-[#3F3F3F] mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                {client.title}
              </h3>
              <p className="text-gray-600 text-sm">
                {client.description}
              </p>
            </div>
          </AnimatedSection>
          )}
        </div>
      </div>
    </section>);

};

// How It Works Section
const ProcessSection = () => {
  const steps = [
  {
    number: "1",
    title: "Submit Your Inquiry",
    description: "Tell us about the type and quantity of electronic equipment you need to dispose of."
  },
  {
    number: "2",
    title: "Assessment",
    description: "Our team evaluates your needs and plans the logistics for collection."
  },
  {
    number: "3",
    title: "Collection & Processing",
    description: "ElectroCycle collects your WEEE and ensures compliant recycling and reporting."
  }];


  return (
    <section id="process" className="section-gap bg-white relative overflow-hidden" data-testid="process-section">
      {/* Background texture */}
      <div className="absolute inset-0 opacity-5">
        <img src={CIRCUIT_TEXTURE} alt="" className="w-full h-full object-cover" />
      </div>
      
      <div className="section-container relative z-10">
        <AnimatedSection className="text-center mb-16">
          <p className="uppercase !font-medium !text-xl mb-3 text-[#69A488]">How It Works</p>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#3F3F3F] mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            SIMPLE THREE-STEP PROCESS
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-gray-600 max-w-2xl mx-auto">
            Getting started with ElectroCycle is straightforward. We handle the complexity so you can focus on your business.
          </p>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
          {/* Connection line for desktop */}
          <div className="hidden md:block absolute top-[60px] left-[16%] right-[16%] h-0.5 bg-[#D2E4D6]"></div>
          
          {steps.map((step, index) =>
          <AnimatedSection key={index} delay={index * 200}>
            <div className="process-step relative z-10 hover:scale-105 transition-transform duration-300" data-testid={`process-step-${index}`}>
              <div className="process-number animate-pulse">
                {step.number}
              </div>
              <h3 className="text-xl font-semibold text-[#3F3F3F] mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                {step.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {step.description}
              </p>
            </div>
          </AnimatedSection>
          )}
        </div>
      </div>
    </section>);

};

// Animated Counter Component
const AnimatedCounter = ({ end, suffix = "", duration = 2000 }) => {
  const { count, ref } = useCounterAnimation(end, duration);
  return (
    <div ref={ref} className="text-4xl font-bold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
      {count}{suffix}
    </div>
  );
};

// Compliance Section
const ComplianceSection = () => {
  return (
    <section className="section-gap text-white relative overflow-hidden" data-testid="compliance-section">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img 
          src="https://customer-assets.emergentagent.com/job_e9d52fdc-bdf3-4925-892d-97563baca200/artifacts/ih6jm0ex_image.png" 
          alt="" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#056839]/85"></div>
      </div>
      
      <div className="section-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-sm font-medium tracking-wide uppercase text-[#D2E4D6] mb-3">Compliance & Reporting</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              DOCUMENTATION YOU CAN TRUST
            </h2>
            <p className="text-lg leading-relaxed text-[#D2E4D6] mb-8">
              ElectroCycle provides reliable documentation and data to support EPR regulatory compliance and internal waste tracking.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-sm bg-[#69A488] flex items-center justify-center flex-shrink-0">
                  <ClipboardCheck size={20} />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Waste Collection and Treatment Certificates</h4>
                  <p className="text-[#D2E4D6] text-sm">Documentation confirming responsible handling and processing of collected WEEE.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-sm bg-[#69A488] flex items-center justify-center flex-shrink-0">
                  <BarChart3 size={20} />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">ESG Reports</h4>
                  <p className="text-[#D2E4D6] text-sm">Data supporting environmental, social and governance reporting requirements.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-sm bg-[#69A488] flex items-center justify-center flex-shrink-0">
                  <FileCheck size={20} />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Waste Tracking</h4>
                  <p className="text-[#D2E4D6] text-sm">Digital tracking of WEEE from collection to final processing.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="relative">
            <div className="bg-white/10 backdrop-blur-sm rounded-sm p-8">
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-4">
                  <AnimatedCounter end={100} suffix="%" />
                  <p className="text-[#D2E4D6] text-sm">Compliance Rate</p>
                </div>
                <div className="text-center p-4">
                  <AnimatedCounter end={25} suffix="+" />
                  <p className="text-[#D2E4D6] text-sm">Tons Processed</p>
                </div>
                <div className="text-center p-4">
                  <AnimatedCounter end={10} suffix="+" />
                  <p className="text-[#D2E4D6] text-sm">Collection Partners</p>
                </div>
                <div className="text-center p-4">
                  <AnimatedCounter end={24} suffix="h" />
                  <p className="text-[#D2E4D6] text-sm">Response Time</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

// FAQ Section
const FAQSection = () => {
  const faqs = [
  {
    question: "What types of electronic equipment do you accept?",
    answer: "We accept a wide range of WEEE including computers, laptops, monitors, printers, servers, networking equipment, mobile phones, televisions, household appliances, and industrial electronic equipment. Contact us for specific items not listed."
  },
  {
    question: "How does the WEEE collection process work?",
    answer: "Simply submit an inquiry through our website or contact us directly. Our team will assess your needs, schedule a convenient pickup time, and collect the equipment from your location. You'll receive documentation for all collected items."
  },
  {
    question: "What compliance documentation do you provide?",
    answer: "We provide comprehensive documentation including collection certificates, recycling certificates, weight manifests, and detailed reports for regulatory compliance and ESG reporting requirements."
  },
  {
    question: "Is there a minimum quantity for collection?",
    answer: "We work with businesses of all sizes. While there's no strict minimum, we recommend scheduling collections when you have a reasonable volume to ensure efficiency. Contact us to discuss your specific situation."
  },
  {
    question: "How do you ensure data security for IT equipment?",
    answer: "All data-bearing devices undergo secure data destruction processes. We provide certificates of destruction upon request, ensuring your sensitive business data is completely and irreversibly erased."
  },
  {
    question: "What areas in Zambia do you service?",
    answer: "ElectroCycle provides pickup services across Zambia. Prices may vary depending on the region. Please contact our team to discuss your specific needs."
  },
  {
    question: "How long does the collection process take?",
    answer: "Once you submit an inquiry, our team typically responds within 24 hours. Collection scheduling depends on your location and volume, but most pickups are completed within one week of confirmation."
  }];


  return (
    <section id="faq" className="section-gap bg-[#F9FAFB]" data-testid="faq-section">
      <div className="section-container">
        <AnimatedSection className="text-center mb-16">
          <p className="uppercase !font-medium !text-lg mb-3 text-[#69A488]">FAQ</p>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#3F3F3F] mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            FREQUENTLY ASKED QUESTIONS
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-gray-600 max-w-2xl mx-auto">
            Find answers to common questions about our WEEE collection and compliance services.
          </p>
        </AnimatedSection>

        <AnimatedSection className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            {faqs.map((faq, index) =>
            <AccordionItem
              key={index}
              value={`item-${index}`}
              className="bg-white border border-gray-100 rounded-sm px-6 data-[state=open]:shadow-sm hover:border-[#69A488] transition-colors duration-200"
              data-testid={`faq-item-${index}`}>

                <AccordionTrigger className="text-left font-semibold text-[#3F3F3F] hover:text-[#056839] py-5" style={{ fontFamily: 'var(--font-heading)' }}>
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </AnimatedSection>
      </div>
    </section>);

};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    full_name: "",
    company_name: "",
    email: "",
    message: "",
    inquiry_type: "pickup"
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.full_name || !formData.company_name || !formData.email) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, formData);
      toast.success("Thank you! Your inquiry has been submitted. We'll be in touch shortly.");
      setFormData({
        full_name: "",
        company_name: "",
        email: "",
        message: "",
        inquiry_type: "pickup"
      });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <section id="contact" className="section-gap bg-[#E4D5B7]" data-testid="contact-section">
      <div className="section-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left - Info */}
          <div>
            <p className="text-sm font-medium tracking-wide uppercase text-[#056839] mb-3">Get In Touch</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#3F3F3F] mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              NEED WEEE COLLECTION OR COMPLIANCE SUPPORT?
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-gray-600 mb-8">
              Submit your inquiry and our team will get back to you within 24 hours to discuss your WEEE management needs.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm bg-[#056839] flex items-center justify-center text-white">
                  <Truck size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-[#3F3F3F]">Nationwide Collection</h4>
                  <p className="text-gray-600 text-sm">We pick up from your location across Zambia</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm bg-[#056839] flex items-center justify-center text-white">
                  <FileCheck size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-[#3F3F3F]">Full Compliance</h4>
                  <p className="text-gray-600 text-sm">Complete documentation provided</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm bg-[#056839] flex items-center justify-center text-white">
                  <Recycle size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-[#3F3F3F]">Responsible Recycling</h4>
                  <p className="text-gray-600 text-sm">Certified processing facilities</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <div className="bg-white rounded-sm shadow-lg p-8" data-testid="contact-form-container">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <label className="block text-sm font-medium text-[#3F3F3F] mb-2">Full Name *</label>
                <Input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  className="h-12 bg-gray-50 border-gray-200 focus:border-[#056839] focus:ring-1 focus:ring-[#056839] rounded-sm"
                  required
                  data-testid="contact-name-input" />

              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#3F3F3F] mb-2">Company Name *</label>
                <Input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  placeholder="Your company name"
                  className="h-12 bg-gray-50 border-gray-200 focus:border-[#056839] focus:ring-1 focus:ring-[#056839] rounded-sm"
                  required
                  data-testid="contact-company-input" />

              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#3F3F3F] mb-2">Business Email *</label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@company.com"
                  className="h-12 bg-gray-50 border-gray-200 focus:border-[#056839] focus:ring-1 focus:ring-[#056839] rounded-sm"
                  required
                  data-testid="contact-email-input" />

              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#3F3F3F] mb-2">Message (Optional)</label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your WEEE management needs..."
                  className="min-h-[120px] bg-gray-50 border-gray-200 focus:border-[#056839] focus:ring-1 focus:ring-[#056839] rounded-sm"
                  data-testid="contact-message-input" />

              </div>

              <Button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="contact-submit-btn">

                <Send size={20} />
                {loading ? "Submitting..." : "Request Pickup or Compliance Support"}
              </Button>
            </form>
            
            {/* Plastic Packaging Info Box */}
            <div className="mt-6 p-4 bg-[#F9FAFB] border border-gray-200 rounded-sm flex items-start gap-3" data-testid="plastic-info-box">
              <div className="w-5 h-5 rounded-full bg-[#69A488] flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-xs font-bold">i</span>
              </div>
              <p className="text-sm text-gray-600">
                Looking for plastic packaging and WEEE EPR compliance services in Zambia? Please contact our partner:{" "}
                <a 
                  href="https://africycle.pro" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#056839] font-medium hover:underline"
                >
                  Africycle Zambia
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

// Footer
const Footer = () => {
  return (
    <footer className="bg-[#3F3F3F] text-white py-12" data-testid="footer">
      <div className="section-container">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <img src={LOGO} alt="ElectroCycle Logo" className="w-[120px] md:w-[160px] h-auto object-contain mb-4" style={{ border: 'none', outline: 'none' }} />
            <p className="text-gray-400 text-sm leading-relaxed">
              Responsible WEEE management and compliance support for businesses in Zambia.
            </p>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>QUICK LINKS</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="#services" className="hover:text-white transition-colors">Services</a></li>
              <li><a href="#clients" className="hover:text-white transition-colors">Who We Work With</a></li>
              <li><a href="#process" className="hover:text-white transition-colors">How It Works</a></li>
              <li><Link to="/about" className="hover:text-white transition-colors">About</Link></li>
              <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
              <li><a href="#contact" className="hover:text-white transition-colors">Contact</a></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>CONTACT</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li>Kitwe, Zambia</li>
              <li>sales@electrocycle.pro</li>
            </ul>
            
            <h4 className="font-semibold mb-3 mt-6" style={{ fontFamily: 'var(--font-heading)' }}>FOLLOW US</h4>
            <a 
              href="https://www.linkedin.com/company/electrocycle-zambia/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              data-testid="linkedin-link"
            >
              <Linkedin size={20} />
              <span className="text-sm">LinkedIn</span>
            </a>
          </div>
        </div>
        
        <div className="border-t border-gray-600 mt-8 pt-8 text-center text-gray-400 text-sm">
          <p>&copy; {new Date().getFullYear()} ElectroCycle Zambia. All rights reserved.</p>
        </div>
      </div>
    </footer>);

};

// Main Landing Page Component
const LandingPage = () => {
  return (
    <div className="min-h-screen" data-testid="landing-page">
      <Header />
      <main>
        <HeroSection />
        <ServicesSection />
        <ClientsSection />
        <ProcessSection />
        <ComplianceSection />
        <FAQSection />
        <ContactSection />
      </main>
      <Footer />
    </div>);

};

export default LandingPage;